import type { Dispatch, SetStateAction } from 'react';
import { type LogFilters, EMPTY_LOG_FILTERS, hasActiveFilters, methodColor } from './logFormat';

interface LogFilterBarProps {
  filters: LogFilters;
  setFilters: Dispatch<SetStateAction<LogFilters>>;
  knownUsers: string[];
}

const METHODS = ['GET', 'POST', 'PATCH', 'DELETE'];

/** Filter controls for the Server Console: method pills, user, type, status, date range. */
export function LogFilterBar({ filters, setFilters, knownUsers }: LogFilterBarProps) {
  const toggleMethod = (method: string) =>
    setFilters(f => ({ ...f, method: f.method === method ? '' : method }));

  return (
    <div className="flex items-center gap-2 flex-wrap mb-6 mt-4">
      {METHODS.map(m => (
        <button
          key={m}
          onClick={() => toggleMethod(m)}
          className={`px-2.5 py-1 rounded-full text-xs font-bold ${
            filters.method === m ? methodColor(m) + ' ring-2 ring-offset-1 ring-td-blue' : 'bg-gray-50 text-gray-400'
          }`}
        >
          {m}
        </button>
      ))}

      <div className="h-4 w-px bg-gray-200 mx-1" />

      <select
        value={filters.user}
        onChange={e => setFilters(f => ({ ...f, user: e.target.value }))}
        className="text-xs border rounded-lg px-2 py-1.5 bg-white"
      >
        <option value="">Alle gebruikers</option>
        {knownUsers.map(u => <option key={u} value={u}>{u}</option>)}
      </select>

      <select
        value={filters.type}
        onChange={e => setFilters(f => ({ ...f, type: e.target.value as LogFilters['type'] }))}
        className="text-xs border rounded-lg px-2 py-1.5 bg-white"
      >
        <option value="all">Alles</option>
        <option value="requests">Requests</option>
        <option value="actions">Acties</option>
        <option value="errors">Fouten</option>
      </select>

      <select
        value={filters.statusClass}
        onChange={e => setFilters(f => ({ ...f, statusClass: e.target.value as LogFilters['statusClass'], status: '' }))}
        className="text-xs border rounded-lg px-2 py-1.5 bg-white"
        aria-label="Statusfilter"
      >
        <option value="">Alle statussen</option>
        <option value="4xx">4xx</option>
        <option value="5xx">5xx</option>
      </select>

      <input
        type="number"
        inputMode="numeric"
        min={100}
        max={599}
        placeholder="Code"
        value={filters.status}
        onChange={e => setFilters(f => ({ ...f, status: e.target.value, statusClass: '' }))}
        className="text-xs border rounded-lg px-2 py-1.5 bg-white w-20"
        aria-label="Exacte statuscode"
      />

      <div className="h-4 w-px bg-gray-200 mx-1" />

      <label className="text-xs text-td-muted">Van</label>
      <input
        type="date"
        value={filters.from}
        onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
        className="text-xs border rounded-lg px-2 py-1.5 bg-white"
      />

      <label className="text-xs text-td-muted">Tot</label>
      <input
        type="date"
        value={filters.to}
        onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
        className="text-xs border rounded-lg px-2 py-1.5 bg-white"
      />

      {hasActiveFilters(filters) && (
        <button
          onClick={() => setFilters(EMPTY_LOG_FILTERS)}
          className="text-xs text-td-blue hover:underline ml-1"
        >
          Reset
        </button>
      )}
    </div>
  );
}
